import {
  Event,
  VenueWithEvents,
  CityEventRow,
  CityEventRowV2,
  EventAttractionRow,
} from "./types";
import { DateRangeValue } from "./constants";
import { cleanOutboundUrl } from "./outbound-urls";
import { getConcertGenresForTags } from "./genre-taxonomy";

// --- Slugify ---

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// --- Data transformation ---

export function transformCityEvents(
  rows: CityEventRow[],
  attractionRows: EventAttractionRow[]
): Event[] {
  // Group attractions by event_id
  const attractionsByEvent = new Map<
    number,
    EventAttractionRow[]
  >();
  for (const row of attractionRows) {
    const existing = attractionsByEvent.get(row.event_id) || [];
    existing.push(row);
    attractionsByEvent.set(row.event_id, existing);
  }

  return rows.map((row) => {
    const eventAttractions = attractionsByEvent.get(row.event_id) || [];
    return {
      id: row.event_id,
      tm_id: row.event_tm_id,
      name: row.event_name,
      slug: row.event_slug ?? slugify(row.event_name),
      url: cleanOutboundUrl(row.event_url),
      image_url: row.event_image_url,
      images: Array.isArray(row.event_images) ? row.event_images : [],
      local_date: row.local_date,
      local_time: row.local_time,
      start_datetime: row.start_datetime,
      status: row.status,
      price_min: row.price_min,
      price_max: row.price_max,
      price_currency: row.price_currency,
      info: row.info,
      please_note: row.please_note ?? null,
      image_focus: row.image_focus ?? null,
      venue: {
        id: row.venue_id,
        tm_id: row.venue_tm_id,
        name: row.venue_name ?? "",
        slug: row.venue_slug ?? (row.venue_name ? slugify(row.venue_name) : ""),
        address: row.venue_address,
        city: row.venue_city,
        latitude: row.venue_latitude ?? 0,
        longitude: row.venue_longitude ?? 0,
        url: cleanOutboundUrl(row.venue_url),
        image_url: row.venue_image_url,
      },
      attractions: eventAttractions.map((a) => ({
        id: a.attraction_id,
        tm_id: a.attraction_tm_id,
        name: a.attraction_name,
        slug: a.attraction_slug,
        image_url: a.attraction_image_url,
        genres: a.attraction_genres ?? [],
        spotify_id: a.attraction_spotify_id,
        external_links: a.attraction_external_links,
      })),
    };
  });
}

export function transformCityEventsV2(rows: CityEventRowV2[]): Event[] {
  return rows.map((row) => ({
    id: row.event_id,
    tm_id: row.event_tm_id,
    name: row.event_name,
    slug: row.event_slug ?? slugify(row.event_name),
    url: cleanOutboundUrl(row.event_url),
    image_url: row.event_image_url,
    images: [],
    local_date: row.local_date,
    local_time: row.local_time,
    start_datetime: row.start_datetime,
    status: row.status,
    price_min: row.price_min,
    price_max: row.price_max,
    price_currency: row.price_currency,
    info: row.info,
    please_note: null,
    image_focus: row.image_focus ?? null,
    venue: {
      id: row.venue_id,
      tm_id: row.venue_tm_id,
      name: row.venue_name,
      slug: row.venue_slug ?? slugify(row.venue_name),
      address: row.venue_address,
      city: row.venue_city,
      latitude: row.venue_latitude,
      longitude: row.venue_longitude,
      url: cleanOutboundUrl(row.venue_url),
      image_url: row.venue_image_url,
    },
    attractions: (row.attractions ?? []).map((a) => ({
      id: a.id,
      tm_id: a.tm_id,
      name: a.name,
      slug: a.slug,
      image_url: a.image_url,
      genres: a.genres ?? [],
      spotify_id: a.spotify_id,
    })),
  }));
}

// --- Venue grouping ---

export function groupEventsByVenue(events: Event[]): VenueWithEvents[] {
  const venueMap = new Map<number, VenueWithEvents>();
  for (const event of events) {
    const existing = venueMap.get(event.venue.id);
    if (existing) {
      existing.events.push(event);
    } else {
      venueMap.set(event.venue.id, {
        venue: event.venue,
        events: [event],
      });
    }
  }
  return Array.from(venueMap.values());
}

// --- Recurring event grouping ---

export interface EventGroup {
  key: string;
  events: Event[];
  /** Total show count when only a subset of events is loaded (lazy-load) */
  totalShowCount?: number;
}

// Strip common Ticketmaster suffixes for grouping purposes
// e.g. "Sounds Fun SHOW 2" → "Sounds Fun", "My Event - Session 3" → "My Event"
function normalizeEventName(name: string): string {
  return name
    .replace(/\s*[-–—]\s*(show|session|performance|set)\s*\d+\s*$/i, "")
    .replace(/\s+(show|session|performance|set)\s*\d+\s*$/i, "")
    .replace(/\s*\(?(show|session|performance|set)\s*\d+\)?\s*$/i, "")
    .replace(/\s*#\d+\s*$/, "")
    .replace(/\s*\(\d+\)\s*$/, "")
    .trim();
}

export function groupRecurringEvents(events: Event[]): EventGroup[] {
  const map = new Map<string, EventGroup>();
  const order: string[] = [];

  for (const event of events) {
    // Group by: same attraction(s) at same venue, OR same normalized name at same venue
    let key: string;
    if (event.attractions.length > 0) {
      const artistKey = event.attractions
        .map((a) => a.id)
        .sort((a, b) => a - b)
        .join(",");
      key = "artists:" + artistKey + "::" + event.venue.id;
    } else {
      key = "name:" + normalizeEventName(event.name) + "::" + event.venue.id;
    }
    const existing = map.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      const group: EventGroup = { key, events: [event] };
      map.set(key, group);
      order.push(key);
    }
  }

  // Sort events within each group by date/time
  for (const group of map.values()) {
    group.events.sort((a, b) => {
      const dateA = a.local_date || "";
      const dateB = b.local_date || "";
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = a.local_time || "";
      const timeB = b.local_time || "";
      return timeA.localeCompare(timeB);
    });
  }

  return order.map((key) => map.get(key)!);
}

// --- Date formatting ---

const DATE_LOCALE_MAP: Record<string, string> = {
  en: "en-NZ",
  "de-CH": "de-CH",
};

function getDateLocale(locale?: string): string {
  return DATE_LOCALE_MAP[locale ?? "en"] ?? "en-NZ";
}

export function formatDate(
  dateStr: string | null,
  locale?: string,
  tba?: string
): string {
  if (!dateStr) return tba ?? "TBA";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString(getDateLocale(locale), {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

export function formatDateRange(
  startDate: string | null,
  endDate: string | null,
  locale?: string,
  tba?: string
): string {
  if (!startDate || !endDate || startDate === endDate) {
    return formatDate(startDate, locale, tba);
  }
  const dl = getDateLocale(locale);
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  const startStr = start.toLocaleDateString(dl, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const endStr = end.toLocaleDateString(dl, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  return `${startStr} – ${endStr}`;
}

/** Convert "HH:MM" or "HH:MM:SS" to minutes from midnight. */
export function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
}

export function formatTime(timeStr: string | null, locale?: string): string {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  // Normalize 24+ hours (late-night festival sets) to 0-23 for display
  const h = parseInt(hours, 10) % 24;
  if (locale === "de-CH") {
    return `${h}:${minutes}`;
  }
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${suffix}`;
}

function formatCurrency(n: number, currency: string | null, locale?: string): string {
  const loc = locale ?? "en";
  return new Intl.NumberFormat(loc === "de-CH" ? "de-CH" : "en-NZ", {
    style: "currency",
    currency: currency ?? "NZD",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatPrice(
  min: number | null,
  max: number | null,
  currency: string | null,
  locale?: string,
  tba?: string
): string {
  if (min == null && max == null) return tba ?? "Price TBA";
  if (min != null && max != null && min !== max) {
    return `${formatCurrency(min, currency, locale)} – ${formatCurrency(max, currency, locale)}`;
  }
  return formatCurrency((min ?? max)!, currency, locale);
}

/** Single-token price for tight slots such as a ticket button: a range
 *  collapses to its floor ("US$25+") so it never has to wrap. */
export function formatPriceCompact(
  min: number | null,
  max: number | null,
  currency: string | null,
  locale?: string,
  tba?: string
): string {
  if (min != null && max != null && min !== max) {
    return `${formatCurrency(min, currency, locale)}+`;
  }
  return formatPrice(min, max, currency, locale, tba);
}

// --- Image Transforms ---

import { getVariantUrl } from "./image-variant-urls";

const R2_HOST = "img.finda.show";

export function getTransformedImageUrl(
  url: string,
  opts: { width: number; quality?: number; resize?: "contain" | "cover" }
): string {
  // R2 images → pre-generated variants
  if (url.includes(R2_HOST)) {
    return getVariantUrl(url, opts.width);
  }

  // Legacy Supabase images (during migration)
  if (url.includes("/storage/v1/object/public/images/")) {
    const match = url.match(/\/storage\/v1\/object\/public\/images\/(.+)$/);
    if (match) {
      return getVariantUrl(`https://${R2_HOST}/${match[1]}`, opts.width);
    }
  }

  return url;
}

// --- Event image helper ---

/** Only return images hosted on our own storage — never external URLs. */
export function isOwnImage(url: string): boolean {
  return url.includes(R2_HOST) || url.includes("/storage/v1/object/public/images/");
}

/** True when the feed supplied nothing but placeholder art for this event, so
 *  a hosted copy of it must not be shown as the event's own image. */
export function hasOnlyFallbackImages(event: Pick<Event, "images">): boolean {
  const images = event.images ?? [];
  return images.length > 0 && images.every((image) => image.fallback === true);
}

export function getEventImage(event: Event): string | null {
  if (
    event.image_url &&
    isOwnImage(event.image_url) &&
    !hasOnlyFallbackImages(event)
  ) {
    return event.image_url;
  }
  // Fallback: first attraction with an own-hosted image
  const attractionImage = event.attractions?.find(
    (a) => a.image_url && isOwnImage(a.image_url)
  )?.image_url;
  return attractionImage ?? null;
}

// --- Filter helpers ---

// Non-music genres from Ticketmaster to hide from display and filters
const HIDDEN_GENRES = new Set([
  // Generic / catch-all
  "Undefined",
  "Miscellaneous",
  "Other",
  "Music",
  // Arts & Theatre
  "Comedy",
  "Theatre",
  "Miscellaneous Theatre",
  "Dance",
  "Opera",
  "Circus & Specialty Acts",
  "Circus",
  "Magic & Illusion",
  "Magic",
  "Puppetry",
  "Variety",
  "Lecture/Seminar",
  "Performance Art",
  "Spectacular",
  "Cabaret",
  "Musical",
  "Ballet",
  "Burlesque",
  "Drama",
  "Drag",
  "Mystery",
  "Cultural",
  // Sports
  "Basketball",
  "Baseball",
  "Football",
  "Soccer",
  "Tennis",
  "Hockey",
  "Wrestling",
  "Boxing",
  "MMA",
  "Cricket",
  "Rugby",
  "Motorsports/Racing",
  "Golf",
  "Volleyball",
  "Equestrian",
  "Rodeo",
  "Bullriding",
  "Skiing/Snowboarding",
  "Surfing",
  "Ice Skating",
  "Skating",
  "Lacrosse",
  "Softball",
  "Martial Arts",
  "Netball",
  "Cycling",
  "Badminton",
  "Swimming",
  "Athletics",
  "Gymnastics",
  "Bowling",
  "Water Polo",
  "Handball",
  "Track & Field",
  "College",
  "High School",
  "Minor League",
  "NBA",
  "WNBA",
  "MLB",
  "MLS",
  "NHL",
  "Rugby League",
  // Film
  "Film",
  "Animation",
  "Documentary",
  // Family
  "Family",
  "Children's Theatre",
  "Ice Shows",
  "Fairs & Festivals",
  // Community / civic / expos
  "Community/Civic",
  "Hobby/Special Interest Expos",
  "Food & Drink",
  "Fashion",
  "Gaming",
  "Esports",
  "Restaurant",
  "Podcast",
  "Urban",
  // Religious (as a non-music-genre label)
  "Religious",
  "Contemporary Christian",
]);

const HIDDEN_GENRES_LOWER = new Set(
  Array.from(HIDDEN_GENRES).map((g) => g.toLowerCase())
);

function isHiddenGenre(genre: string): boolean {
  return HIDDEN_GENRES_LOWER.has(genre.toLowerCase());
}

export function isDisplayableGenre(genre: string | null): boolean {
  if (!genre) return false;
  return !isHiddenGenre(genre);
}

/** A saved placeholder must not count as completed artist enrichment. */
export function isQualifyingArtistGenre(genre: string | null | undefined): boolean {
  const tag = genre?.trim();
  return !!tag && !/^(unknown|n\/a|none)$/i.test(tag) && isDisplayableGenre(tag);
}

/** True when at least one attraction has a spotify_id (i.e. it's a music event). */
function isMusicEvent(event: Event): boolean {
  return event.attractions.some((a) => a.spotify_id);
}

export const OTHER_GENRE = "Other";

/** Get all displayable genres for an event — event genre + all attraction genres/sub_genres, deduped case-insensitively. */
export function getEventGenres(event: Event): string[] {
  const seen = new Set<string>();
  const genres: string[] = [];
  const add = (g: string | null | undefined) => {
    if (!g || !isDisplayableGenre(g)) return;
    const key = g.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      genres.push(g);
    }
  };
  for (const a of event.attractions ?? []) {
    for (const g of a.genres ?? []) {
      add(g);
    }
  }
  return genres;
}

// Filters include broad concert families without changing the actual artist
// tags shown on event cards. Count each family once per event, even when
// several artists or subgenres match it.
function getEventFilterGenres(event: Event): string[] {
  const tags = getEventGenres(event);
  const genres = new Map(tags.map((tag) => [tag.toLowerCase(), tag]));
  for (const family of getConcertGenresForTags(tags)) {
    genres.set(family.name.toLowerCase(), family.name);
  }
  return [...genres.values()];
}

/** Returns a map of genre (canonical casing) → event count. */
export function getGenreCounts(events: Event[]): Map<string, number> {
  const counts = new Map<string, number>(); // lowercase -> count
  const canonical = new Map<string, string>(); // lowercase -> best casing
  const canonicalCounts = new Map<string, Map<string, number>>(); // lowercase -> { form -> count }
  let otherCount = 0;
  for (const e of events) {
    const genres = getEventFilterGenres(e);
    if (genres.length > 0) {
      for (const g of genres) {
        const key = g.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
        if (!canonicalCounts.has(key)) canonicalCounts.set(key, new Map());
        const forms = canonicalCounts.get(key)!;
        forms.set(g, (forms.get(g) || 0) + 1);
      }
    } else if (!isMusicEvent(e)) {
      otherCount++;
    }
  }
  // Resolve canonical casings
  for (const [key, forms] of canonicalCounts) {
    const best = Array.from(forms.entries()).sort((a, b) => b[1] - a[1])[0][0];
    canonical.set(key, best);
  }
  const result = new Map<string, number>();
  for (const [key, count] of counts) {
    result.set(canonical.get(key)!, count);
  }
  if (otherCount > 0) {
    result.set(OTHER_GENRE, otherCount);
  }
  return result;
}

export function getUniqueGenres(events: Event[]): string[] {
  const counts = new Map<string, number>(); // lowercase -> count
  const canonical = new Map<string, Map<string, number>>(); // lowercase -> { form -> count }
  let otherCount = 0;
  for (const e of events) {
    const genres = getEventFilterGenres(e);
    if (genres.length > 0) {
      for (const g of genres) {
        const key = g.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
        if (!canonical.has(key)) canonical.set(key, new Map());
        const forms = canonical.get(key)!;
        forms.set(g, (forms.get(g) || 0) + 1);
      }
    } else if (!isMusicEvent(e)) {
      otherCount++;
    }
  }
  const sorted = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => {
      // Pick the most common casing for display
      const forms = canonical.get(key)!;
      return Array.from(forms.entries()).sort((a, b) => b[1] - a[1])[0][0];
    });
  if (otherCount > 0) {
    sorted.push(OTHER_GENRE);
  }
  return sorted;
}

/** Collect the raw (hidden) genres from events in the "Other" bucket, sorted by frequency. */
export function getOtherSubGenres(events: Event[]): string[] {
  const counts = new Map<string, number>();
  for (const e of events) {
    const displayable = getEventGenres(e);
    if (displayable.length > 0 || isMusicEvent(e)) continue;
    // This is an "Other" event — collect its raw genres
    const raw: (string | null | undefined)[] = [
      ...e.attractions.flatMap((a) => a.genres ?? []),
    ];
    for (const g of raw) {
      if (!g) continue;
      const lower = g.toLowerCase();
      if (lower !== "undefined" && lower !== "miscellaneous" && lower !== "other" && lower !== "music") {
        // Deduplicate case-insensitively, keep first-seen form
        const existing = Array.from(counts.keys()).find((k) => k.toLowerCase() === lower);
        counts.set(existing || g, (counts.get(existing || g) || 0) + 1);
      }
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([genre]) => genre);
}

export function filterByGenres(
  events: Event[],
  selectedGenres: string[],
  matchMode: "or" | "and" = "or"
): Event[] {
  if (selectedGenres.length === 0) {
    // "All" is an inventory view, not a metadata-completeness filter. A
    // source-confirmed show must remain visible while uncertain artist genres
    // or provider accounts are intentionally left blank.
    return events;
  }
  const includesOther = selectedGenres.includes(OTHER_GENRE);
  const specificGenres = selectedGenres
    .filter((g) => g !== OTHER_GENRE && !isHiddenGenre(g))
    .map((g) => g.toLowerCase());
  // Hidden genres selected from the "Other" breakout
  const hiddenGenres = selectedGenres
    .filter((g) => g !== OTHER_GENRE && isHiddenGenre(g))
    .map((g) => g.toLowerCase());

  return events.filter((e) => {
    const genres = getEventFilterGenres(e);
    const genresLower = genres.map((g) => g.toLowerCase());

    // Specific genre matching
    if (specificGenres.length > 0) {
      if (matchMode === "and") {
        // AND mode: event must have ALL selected specific genres
        const hasAll = specificGenres.every((sg) => genresLower.includes(sg));
        if (hasAll) return true;
      } else {
        // OR mode: event matches if ANY selected genre is present
        if (genresLower.some((g) => specificGenres.includes(g))) return true;
      }
    }

    // Match hidden genre selections against raw event genres (always OR)
    if (hiddenGenres.length > 0 && genres.length === 0 && !isMusicEvent(e)) {
      const raw = [
        ...e.attractions.flatMap((a) => a.genres ?? []),
      ].filter(Boolean) as string[];
      if (raw.some((g) => hiddenGenres.includes(g.toLowerCase()))) {
        return true;
      }
    }
    // "Other" bucket: no genres AND no spotify (always OR-combined)
    if (includesOther && genres.length === 0 && !isMusicEvent(e)) {
      return true;
    }
    // Music events without genres only show under "All genres", not specific filters
    return false;
  });
}

/** Get today as "YYYY-MM-DD" in the given IANA timezone (falls back to browser default). */
function todayInTz(tz?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  if (tz) opts.timeZone = tz;
  // en-CA locale formats as YYYY-MM-DD
  return new Intl.DateTimeFormat("en-CA", opts).format(new Date());
}


/** Add days to a "YYYY-MM-DD" string and return "YYYY-MM-DD". */
function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Get day of week (0=Sun..6=Sat) for a "YYYY-MM-DD" string. */
function dayOfWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).getDay();
}

export function filterByDateRange(
  events: Event[],
  range: DateRangeValue,
  customStart?: string | null,
  customEnd?: string | null,
  timezone?: string
): Event[] {
  if (range === "all") return events;

  const today = todayInTz(timezone);
  const dow = dayOfWeek(today);

  return events.filter((e) => {
    const d = e.local_date;
    if (!d) return false;

    switch (range) {
      case "today":
        return d === today;
      case "weekend": {
        // Fri–Sun in the city → this weekend; Mon–Thu → next weekend
        // "friday" is the anchor: the Friday starting the weekend
        const friday =
          dow === 5 ? today :              // Friday
          dow === 6 ? addDays(today, -1) : // Saturday
          dow === 0 ? addDays(today, -2) : // Sunday
          addDays(today, 5 - dow);         // Mon–Thu → next Friday
        const sunday = addDays(friday, 2);
        return d >= friday && d <= sunday && d >= today;
      }
      case "week": {
        const endOfWeek = addDays(today, 7 - dow);
        return d >= today && d <= endOfWeek;
      }
      case "month": {
        const [y, m] = today.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const endOfMonth = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
        return d >= today && d <= endOfMonth;
      }
      case "custom": {
        if (!customStart && !customEnd) return true;
        if (customStart && d < customStart) return false;
        if (customEnd && d > customEnd) return false;
        return true;
      }
      default:
        return true;
    }
  });
}

export type TimeOfDay = "morning" | "afternoon" | "evening";

export function filterByTimeOfDay(
  events: Event[],
  selectedTime: TimeOfDay | null
): Event[] {
  if (!selectedTime) return events;

  return events.filter((e) => {
    if (!e.local_time) return true;
    const hour = parseInt(e.local_time.split(":")[0], 10);
    if (selectedTime === "morning") return hour < 12;
    if (selectedTime === "afternoon") return hour >= 12 && hour < 17;
    return hour >= 17; // evening
  });
}

// --- Festival date range formatting ---

/**
 * Format an array of day-dates into compact date ranges.
 * Groups consecutive dates (gap > 2 days = new range).
 * Example: ["2026-04-23","2026-04-24","2026-04-25","2026-04-26","2026-04-30","2026-05-01","2026-05-02","2026-05-03"]
 * → "Apr 23–26 & Apr 30 – May 3"
 */
export function formatFestivalDateRanges(
  dayDates: string[],
  locale?: string
): string {
  if (dayDates.length === 0) return "";

  const sorted = [...dayDates].sort();
  const dates = sorted.map((d) => new Date(d + "T00:00:00"));

  // Group consecutive dates (gap > 2 days = new range)
  const ranges: { start: Date; end: Date }[] = [];
  let rangeStart = dates[0];
  let rangeEnd = dates[0];

  for (let i = 1; i < dates.length; i++) {
    const diff =
      (dates[i].getTime() - rangeEnd.getTime()) / (1000 * 60 * 60 * 24);
    if (diff <= 2) {
      rangeEnd = dates[i];
    } else {
      ranges.push({ start: rangeStart, end: rangeEnd });
      rangeStart = dates[i];
      rangeEnd = dates[i];
    }
  }
  ranges.push({ start: rangeStart, end: rangeEnd });

  const dl = locale === "de-CH" ? "de-CH" : "en-NZ";

  function formatRange(start: Date, end: Date): string {
    if (start.getTime() === end.getTime()) {
      return start.toLocaleDateString(dl, { month: "short", day: "numeric" });
    }
    if (start.getMonth() === end.getMonth()) {
      const month = start.toLocaleDateString(dl, { month: "short" });
      return `${month} ${start.getDate()}–${end.getDate()}`;
    }
    const startStr = start.toLocaleDateString(dl, {
      month: "short",
      day: "numeric",
    });
    const endStr = end.toLocaleDateString(dl, {
      month: "short",
      day: "numeric",
    });
    return `${startStr} – ${endStr}`;
  }

  return ranges.map((r) => formatRange(r.start, r.end)).join(" & ");
}

// --- Artist names ---

export function getArtistNames(event: Event): string {
  if (event.attractions.length > 0) {
    return event.attractions.map((a) => a.name).join(" / ");
  }
  return event.name;
}
